import { useCallback, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useAutosave } from '../../hooks/useAutosave';
import { applyServerFieldErrors } from '../../services/driverApplicationService';

/**
 * Shared plumbing for wizard steps: react-hook-form + debounced autosave +
 * "complete" save with server error mapping.
 *
 * @param {Object} options
 * @param {string}   options.stepId
 * @param {Object}   options.defaultValues
 * @param {Function} options.toPayload       (values) => payload for the API
 * @param {Function} options.onSaveDraft     async (payload) => void
 * @param {Function} options.onComplete      async (payload) => { ok, errors?, message? }
 * @param {boolean}  options.locked
 * @param {Function} [options.validate]      (values) => [{ name, message }] extra client rules
 */
export const useStepForm = ({ stepId, defaultValues, toPayload, onSaveDraft, onComplete, locked = false, validate }) => {
  // The ErrorSummary takes focus on a failed attempt, so RHF should not also jump to a field.
  const form = useForm({ defaultValues, mode: 'onSubmit', reValidateMode: 'onChange', shouldFocusError: false });
  const { control, handleSubmit, setError, clearErrors, getValues, getFieldState, formState } = form;
  const values = useWatch({ control });
  const [submitting, setSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState('');
  // Bumped on every failed Next so the ErrorSummary moves focus only then, never while typing.
  const [failedAttempts, setFailedAttempts] = useState(0);

  const autosave = useAutosave({
    data: values,
    enabled: !locked,
    key: stepId,
    onSave: async (current) => onSaveDraft(toPayload(current))
  });

  // `keepFieldErrors`: when field rules already failed, don't replace their
  // (more specific) message with a cross-field one for the same field.
  const applyExtraRules = useCallback((current, { keepFieldErrors = false } = {}) => {
    const extra = typeof validate === 'function' ? validate(current) : [];
    (extra || []).forEach(({ name, message }) => {
      if (keepFieldErrors && getFieldState(name).error) return;
      setError(name, { type: 'manual', message });
    });
    return Boolean(extra && extra.length);
  }, [validate, setError, getFieldState]);

  const submitNext = useCallback(() => handleSubmit(async (current) => {
    setServerMessage('');
    clearErrors();

    if (applyExtraRules(current)) {
      setFailedAttempts((n) => n + 1);
      return;
    }

    setSubmitting(true);
    try {
      const result = await onComplete(toPayload(current));
      if (result?.ok) {
        autosave.markSaved();
      } else {
        const applied = applyServerFieldErrors({ details: result?.errors }, setError);
        setServerMessage(result?.message || (applied ? 'Please fix the highlighted fields.' : 'We could not save this step. Please try again.'));
        setFailedAttempts((n) => n + 1);
      }
    } finally {
      setSubmitting(false);
    }
  }, () => {
    // Field rules failed: still run the step's cross-field rules so the summary lists everything at once.
    applyExtraRules(getValues(), { keepFieldErrors: true });
    setFailedAttempts((n) => n + 1);
  })(), [handleSubmit, clearErrors, applyExtraRules, getValues, setError, onComplete, toPayload, autosave]);

  return {
    form,
    values,
    errors: formState.errors,
    autosave,
    submitting,
    serverMessage,
    failedAttempts,
    submitNext
  };
};

export default useStepForm;
